import React, { useEffect, useState } from "react";
import {
  Moon,
  Sun,
  Save,
  Wallet,
  TrendingUp,
  Sparkles,
  Bell,
  ChevronRight,
  Plane,
  Plus,
  ShieldCheck,
  LayoutDashboard,
  BarChart3,
  User,
  CreditCard,
  X,
  Trash2,
  Trash,
} from "lucide-react";
import {
  createCalculateRewardValue,
  getValuationAnalytics,
} from "../../../services/Front/dashboardService";
import { decryptData } from "../../../utils/crypto";
import { notify } from "../../../components/Common/Alerts";
import Utils from "../../../utils";
import { useSelector } from "react-redux";
import CONFIG from "../../../config";
import { getInitials } from "../../../utils/function";
import { getAllCardsList } from "../../../services/Admin/commonService";
import {
  createOrUpdateUserPointProfileUpdate,
  deleteUserPoint,
} from "../../../services/Front/userPointService";
import moment from "moment";
import ConfirmModal from "../../../components/Common/ConfirmModal";

const PointWallet = () => {
  let auth = useSelector((x) => x.auth.value);
  let authData = JSON.parse(localStorage.getItem(CONFIG.login_data)) || {};
  const [darkMode, setDarkMode] = useState(false);
  const [isAddingInline, setIsAddingInline] = useState(false);
  const [cardAllList, setCardAllList] = useState([]);
  const [profileList, setProfileList] = useState("");
  const [selectedCards, setSelectedCards] = useState([]);
  const [deleteShow, setDeleteShow] = useState(false);
  const [deleteId, setDeleteId] = useState("");
  const [btnDeleteLoading, setBtnDeleteLoading] = useState(false);
  const [search, setSearch] = useState("");

  const strategy = profileList?.strategy_center;
  const availableCards =
    cardAllList?.filter(
      (card) =>
        !selectedCards?.some(
          (selected) => Number(selected.card_id) === Number(card.id),
        ),
    ) || [];

  useEffect(() => {
    if (profileList?.cards) {
      setSelectedCards(profileList.cards);
    }
  }, [profileList]);

  const fetchValuationAnalytics = async () => {
    try {
      const response = await getValuationAnalytics();
      const decrypted = decryptData(response.data.data);
      setProfileList(decrypted);
    } catch (err) {
      notify.error(Utils.getErrorMessage(err));
    }
  };

  useEffect(() => {
    fetchValuationAnalytics();
  }, []);

  useEffect(() => {
    getAllCardsList()
      .then((response) => {
        const decrypted = decryptData(response.data.data);
        setCardAllList(decrypted.data);
      })
      .catch((err) => {
        notify.error(Utils.getErrorMessage(err));
      });
  }, []);

  const calculateTravelValue = async (cardId, points) => {
    try {
      if (!points || Number(points) <= 0) return;

      const response = await createCalculateRewardValue({
        card_id: cardId,
        points,
      });

      const decrypted = decryptData(response.data.data);

      setSelectedCards((prev) =>
        prev.map((card) =>
          Number(card.card_id) === Number(cardId)
            ? {
                ...card,
                latest_min: decrypted?.estimated_min_value || 0,
                latest_max: decrypted?.estimated_max_value || 0,
                travel_cpp: decrypted?.travel_cpp || 0,
              }
            : card,
        ),
      );
    } catch (error) {
      notify.error(Utils.getErrorMessage(error));
    }
  };

  const handleSaveCard = async (card) => {
    try {
      const response = await createOrUpdateUserPointProfileUpdate({
        card_id: card.card_id,
        points_balance: card.latest_points,
      });
      const decrypted = decryptData(response.data.data);
      await calculateTravelValue(card.card_id, card.latest_points);
      await fetchValuationAnalytics();
      notify.success(decrypted.message);
    } catch (error) {
      notify.error(Utils.getErrorMessage(error));
    }
  };

  const handlePointChange = (cardId, value) => {
    setSelectedCards((prev) =>
      prev.map((card) =>
        Number(card.card_id) === Number(cardId)
          ? {
              ...card,
              latest_points: value,
            }
          : card,
      ),
    );
  };

  const handleAddCard = (card) => {
    const exists = selectedCards?.some(
      (x) => Number(x.card_id) === Number(card.id),
    );

    if (exists) return;

    setSelectedCards((prev) => [
      ...prev,
      {
        card_id: card.id,
        name: card.name,
        bank: card.bank,
        reward_program_name: card.reward_program_name,
        latest_points: "",
        latest_min: 0,
        latest_max: 0,
        history: [],
      },
    ]);
  };

  const handleDeleteCard = async (e, cardId) => {
    e.preventDefault();

    if (!deleteId) {
      setSelectedCards((prev) =>
        prev.filter((card) => Number(card.card_id) !== Number(cardId)),
      );
      setDeleteShow(false);
      return;
    }

    try {
      setBtnDeleteLoading(true);
      const response = await deleteUserPoint({ card_id: deleteId });
      let decrypt = decryptData(response.data.data);
      notify.success(decrypt.message);
      await fetchValuationAnalytics();
      setDeleteShow(false);
    } catch (err) {
      notify.error(Utils.getErrorMessage(err));
    } finally {
      setBtnDeleteLoading(false);
    }
  };

  const filteredCards = availableCards?.filter((card) => {
    const q = search.toLowerCase();
    return (
      card.name?.toLowerCase().includes(q) ||
      card.reward_program_name?.toLowerCase().includes(q) ||
      card.bank?.toLowerCase().includes(q)
    );
  });

  const priorityOrder = [
    "american express",
    "chase",
    "capital one",
    "capital",
  ];

  const sortedFilteredCards = [...(filteredCards || [])].sort((a, b) => {
    const aBank = (a.bank || "").toLowerCase();
    const bBank = (b.bank || "").toLowerCase();

    const aIndex = priorityOrder.findIndex((p) => aBank.includes(p));
    const bIndex = priorityOrder.findIndex((p) => bBank.includes(p));

    const aPriority = aIndex === -1 ? Infinity : aIndex;
    const bPriority = bIndex === -1 ? Infinity : bIndex;

    return aPriority - bPriority;
  });

  return (
    <div className="bg-[#F4F6F9] dark:bg-[#0B111E] min-h-screen font-sans pb-24 md:pb-12 transition-colors duration-300">
      <div className="max-w-[1600px] mx-auto p-3 sm:p-6 lg:p-8 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
        <div className="lg:col-span-4 xl:col-span-3 space-y-6 w-full lg:sticky lg:top-6 h-fit">
          <div className="rounded-3xl bg-gradient-to-br from-[#CA9E27] via-[#D4AF37] to-[#B38F1E] p-6 text-white shadow-xl shadow-voyage-cyan-600/10 relative overflow-hidden group">
            <div className="absolute -right-10 -top-10 w-40 h-40 bg-white/10 rounded-full blur-2xl group-hover:bg-white/15 transition-all duration-500" />

            <div className="flex items-center gap-4 relative z-10">
              <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-white/15 backdrop-blur-md border border-white/20 flex items-center justify-center text-lg md:text-xl font-black shadow-inner tracking-wider">
                {getInitials(authData?.user?.full_name) || "DP"}
              </div>
              <div className="min-w-0 flex-1">
                <h1 className="text-xl md:text-2xl font-black tracking-tight truncate leading-tight">
                  {authData?.user?.full_name || "Disha Patel"}
                </h1>
                <div className="flex items-center gap-2 mt-1">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-300 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-400"></span>
                  </span>
                  <p className="text-[10px] font-extrabold uppercase tracking-widest text-white/90">
                    Rewards Explorer
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3.5 mt-8 relative z-10">
              <div className="rounded-2xl bg-white/10 backdrop-blur-xs p-4 border border-white/10 shadow-xs hover:bg-white/15 transition-colors">
                <p className="text-[10px] uppercase tracking-widest font-black text-white/80">
                  Total Points
                </p>
                <h2 className="mt-1.5 text-xl md:text-2xl font-black tracking-tight text-white">
                  {profileList?.total_points?.toLocaleString() || "0"}
                </h2>
              </div>

              <div className="rounded-2xl bg-white/10 backdrop-blur-xs p-4 border border-white/10 shadow-xs hover:bg-white/15 transition-colors">
                <p className="text-[10px] uppercase tracking-widest font-black text-white/80">
                  Est. Value
                </p>
                <h2 className="mt-1.5 text-base sm:text-lg md:text-xl font-black tracking-tight text-white break-words leading-tight">
                  $
                  {profileList?.total_estimated_min_value?.toLocaleString() ||
                    "0"}{" "}
                  - $
                  {profileList?.total_estimated_max_value?.toLocaleString() ||
                    "0"}
                </h2>
              </div>
            </div>
          </div>

          <div className="rounded-3xl bg-white dark:bg-[#0F172A] p-6 shadow-xs border border-slate-100 dark:border-slate-800/60 transition-colors duration-300">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-9 h-9 rounded-xl  dark:bg-voyage-cyan text-[#D4AF37] flex items-center justify-center shadow-xs">
                <TrendingUp size={18} className="stroke-[2.5]" />
              </div>
              <h2 className="text-xs font-black text-slate-800 dark:text-slate-200 uppercase tracking-widest">
                Strategy Center
              </h2>
            </div>

            <div className="space-y-4">
              {[
                {
                  label: "Strongest Program",
                  val: strategy?.strongest_program || "-",
                  icon: <Sparkles size={16} />,
                },
                {
                  label: "Best Card",
                  val: strategy?.best_card?.card_name || "-",
                  icon: <CreditCard size={16} />,
                },
                {
                  label: "Best CPP",
                  val: strategy?.best_cpp ? `${strategy.best_cpp} CPP` : "-",
                  icon: <TrendingUp size={16} />,
                },
                {
                  label: "Travel Value",
                  val: strategy?.highest_travel_value
                    ? `$${strategy.highest_travel_value}`
                    : "-",
                  icon: <Plane size={16} />,
                },
              ].map((insight, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between p-3 rounded-2xl bg-slate-50/50 dark:bg-slate-900/40 border border-transparent hover:border-slate-100 dark:hover:border-slate-800/50 hover:bg-white dark:hover:bg-slate-900 transition-all duration-200"
                >
                  <div className="min-w-0 flex-1 pr-2">
                    <p className="text-[10px] uppercase tracking-widest font-black text-slate-400 dark:text-slate-500">
                      {insight.label}
                    </p>
                    <h3 className="mt-1 text-sm md:text-base font-bold text-slate-800 dark:text-slate-100 truncate">
                      {insight.val}
                    </h3>
                  </div>
                  <div className="w-8 h-8 rounded-xl bg-white dark:bg-slate-800/80 shadow-xs border border-slate-100 dark:border-slate-700/50 flex items-center justify-center shrink-0">
                    {insight.icon}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Side: Rewards Wallet Main Arena */}
        {/* Adjusted column span ratios across screen breakouts to widen the list viewport cleanly */}
        <div className="lg:col-span-8 xl:col-span-9 space-y-6 w-full">
          <div className="rounded-3xl bg-white dark:bg-[#0F172A] p-5 md:p-8 shadow-xs border border-slate-100 dark:border-slate-800/80 transition-all duration-300">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-black text-slate-900 dark:text-white tracking-tight">
                  Rewards Wallet
                </h2>
                <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mt-1">
                  {moment(profileList?.last_updated_at)
                    .fromNow()
                    .toUpperCase()}{" "}
                </p>
              </div>

              {!isAddingInline && (
                <button
                  onClick={() => setIsAddingInline(true)}
                  className="flex items-center gap-1.5 px-4 h-10 rounded-xl bg-voyage-cyan hover:bg-voyage-cyan text-white text-xs font-black tracking-wide shadow-sm shadow-voyage-cyan-500/10 active:scale-95 transition-all"
                >
                  <Plus size={16} className="stroke-[2.5]" />
                  <span className="hidden sm:inline">Add New Card</span>
                </button>
              )}
            </div>

            <div className="space-y-4">
              {isAddingInline ? (
                <div className="rounded-2xl border-2 border-dashed border-voyage-cyan dark:border-voyage-cyan bg-voyage-cyan p-5 animate-in fade-in slide-in-from-top-3 duration-200">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <CreditCard size={16} className="text-voyage-cyan-500" />
                      <h3 className="text-xs font-black uppercase tracking-widest text-slate-700 dark:text-slate-300">
                        Select a Card to Link
                      </h3>
                    </div>
                    <button
                      onClick={() => setIsAddingInline(false)}
                      className="p-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                    >
                      <X size={14} />
                    </button>
                  </div>

                  {/* Enhanced Grid Breakpoints: Upgraded to 4 columns on large displays to fill horizontal workspace */}
                  <div className="space-y-4">
                    {/* 🔍 Search Bar */}
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search cards, banks, rewards..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#0A101D] text-sm focus:outline-none focus:ring-2 focus:ring-voyage-cyan-500/40"
                      />
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">
                        🔍
                      </span>
                    </div>

                    {/* 🧾 Grid */}
                    {sortedFilteredCards?.length > 0 ? (
                      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4">
                        {sortedFilteredCards?.map((availableCard, index) => (
                          <button
                            key={index}
                            onClick={() => {
                              handleAddCard(availableCard);
                              setIsAddingInline(false);
                            }}
                            className="group text-left p-3.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#0A101D] hover:border-voyage-cyan-500/70 hover:shadow-lg transition-all duration-200 active:scale-[0.97] flex flex-col justify-between min-h-[100px]"
                          >
                            <div className="min-w-0 w-full">
                              <h4 className="text-xs sm:text-sm font-semibold text-slate-800 dark:text-slate-200 group-hover:text-voyage-cyan-500 transition-colors leading-snug line-clamp-2 sm:line-clamp-1">
                                {availableCard.name}
                              </h4>
                              <p className="text-[10px] text-slate-400 mt-1 truncate">
                                {availableCard.reward_program_name}
                              </p>
                            </div>

                            <div className="flex items-center justify-between mt-3 pt-2 border-t border-slate-100 dark:border-slate-800/50 text-[9px] font-semibold uppercase tracking-wide text-slate-400">
                              <span>{availableCard.bank || "Points"}</span>
                              <Plus
                                size={14}
                                className="text-voyage-cyan-500 opacity-0 group-hover:opacity-100 transition-all duration-200 group-hover:translate-x-0.5"
                              />
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : (
                      /* 🚫 Empty State */
                      <div className="text-center py-10 text-slate-400 text-sm">
                        No cards found 😕
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setIsAddingInline(true)}
                  className="w-full py-4 rounded-2xl border-2 border-dashed border-slate-200 dark:border-slate-800/80 hover:border-voyage-cyan-500/40 dark:hover:border-voyage-cyan-500/30 bg-slate-50/30 dark:bg-transparent text-slate-400 hover:text-voyage-cyan-500 flex items-center justify-center gap-2 text-xs font-bold tracking-wide transition-all group"
                >
                  <Plus
                    size={16}
                    className="group-hover:scale-110 transition-transform"
                  />
                  <span>Link Another Rewards Program</span>
                </button>
              )}

              {/* Individual Linked Program Cards */}
              {selectedCards?.map((card) => (
                <div
                  key={card.card_id}
                  className="rounded-2xl border border-slate-100 dark:border-slate-800/60 bg-slate-50/50 dark:bg-[#0A101D]/60 p-5 md:p-6 hover:shadow-xs transition-shadow"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    {/* Meta Info */}
                    <div className="min-w-0 flex-1">
                      <span className="text-[9px] font-black uppercase tracking-widest text-voyage-cyan-500 dark:text-[#D4AF37]">
                        {card.bank}
                      </span>
                      <h3 className="text-base md:text-lg font-black text-slate-900 dark:text-white tracking-tight mt-0.5 truncate">
                        {card.name}
                      </h3>
                      <p className="text-xs font-medium text-slate-400 dark:text-slate-500 mt-0.5 truncate">
                        {card.reward_program_name}
                      </p>
                    </div>

                    {/* Balance Input Area - Rendered cleanly with horizontal actions */}
                    <div className="w-full sm:w-auto shrink-0">
                      <div className="flex items-center justify-between mb-1.5 pl-0.5">
                        <label className="text-[10px] uppercase tracking-widest font-black text-slate-400 dark:text-slate-500 block">
                          Current Balance
                        </label>
                      </div>

                      <div className="flex items-center gap-2">
                        {/* Input Box */}
                        <div className="relative flex-1 sm:w-48 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#0F172A] shadow-xs group/input focus-within:border-voyage-cyan-500/50 transition-colors">
                          <input
                            type="number"
                            value={card.latest_points || ""}
                            onChange={(e) => {
                              handlePointChange(card.card_id, e.target.value);
                            }}
                            className="w-full h-10 px-3 text-sm font-black text-slate-900 dark:text-white bg-transparent outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            placeholder="0"
                          />
                        </div>

                        {/* Action Row */}
                        <div className="flex items-center gap-1.5 h-10">
                          {/* SAVE BUTTON */}
                          <div className="relative group/save h-full">
                            <button
                              onClick={() => handleSaveCard(card)}
                              className="h-10 w-10 bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-500/10 dark:hover:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 rounded-xl flex items-center justify-center transition-all duration-200 active:scale-95"
                            >
                              <Save
                                size={16}
                                className="group-hover/save:scale-105 transition-transform"
                              />
                            </button>

                            <div className="absolute bottom-full right-1/2 translate-x-1/2 mb-2 hidden group-hover/save:block bg-slate-900 dark:bg-slate-800 text-white text-[10px] font-black px-2.5 py-1.5 rounded-lg shadow-lg whitespace-nowrap pointer-events-none z-20">
                              Update Balance
                            </div>
                          </div>

                          {/* DELETE BUTTON */}
                          <div className="relative group/delete h-full">
                            <button
                              onClick={() => {
                                setDeleteShow(true);
                                setDeleteId(card?.card_id);
                              }}
                              className="h-10 w-10 bg-rose-50 hover:bg-rose-100 dark:bg-rose-500/10 dark:hover:bg-rose-500/20 text-rose-600 dark:text-rose-400 rounded-xl flex items-center justify-center transition-all duration-200 active:scale-95"
                            >
                              <Trash
                                size={16}
                                className="group-hover/delete:scale-105 transition-transform"
                              />
                            </button>

                            <div className="absolute bottom-full right-1/2 translate-x-1/2 mb-2 hidden group-hover/delete:block bg-slate-900 dark:bg-slate-800 text-white text-[10px] font-black px-2.5 py-1.5 rounded-lg shadow-lg whitespace-nowrap pointer-events-none z-20">
                              Delete Card
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Evaluation Metrics Footer */}
                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-200/60 dark:border-slate-800/50">
                    <div>
                      <p className="text-[9px] uppercase font-black tracking-wider text-slate-400 dark:text-slate-500">
                        Est. Travel Value
                      </p>
                      <h4 className="text-sm md:text-base font-black text-emerald-500 dark:text-emerald-400 mt-0.5">
                        ${Number(card.latest_max).toLocaleString()}
                      </h4>
                    </div>

                    <div className="text-right">
                      <p className="text-[9px] uppercase font-black tracking-wider text-slate-400 dark:text-slate-500">
                        Status
                      </p>
                      <div className="flex items-center gap-1.5 mt-1 justify-end">
                        <span className="relative flex h-1.5 w-1.5">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span>
                        </span>
                        <span className="text-[11px] font-black text-slate-600 dark:text-slate-400">
                          Active
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        open={deleteShow}
        onClose={() => setDeleteShow(false)}
        onConfirm={handleDeleteCard}
        loading={btnDeleteLoading}
        titleKey={"Delete Card"}
        descriptionKey={`Are you sure you want to remove ${deleteId?.full_name || "this"}?`}
        confirmText="Yes, Delete"
        icon={<Trash2 size={40} className="text-red-500" />}
      />
    </div>
  );
};

export default PointWallet;
